import { roundCurrency } from "@/lib/formulas";
import {
  FILING_STATUS_OPTIONS,
  PAYROLL_FREQUENCY_OPTIONS,
  PERIODS_PER_YEAR,
  K401_LIMIT,
  DEFAULTS,
} from "@/lib/us/constants";
import { calcFederalIncomeTax, getStandardDeduction } from "@/lib/us/tax";
import { calcSocialSecurity, calcMedicare } from "@/lib/us/payroll";
import { formatPercent } from "@/lib/us/format";
import { STATE_OPTIONS, NO_TAX_STATES, calcStateTax } from "./us/tax-data";

// ============================================================
// FYUNOR — US Salary Calculator
// Estimates take-home pay: gross → pre-tax deductions → FICA →
// federal income tax → state income tax → post-tax deductions → net.
// ============================================================

// Salary pay-frequency options exclude daily and hourly (those are
// handled by the hourly-wage toggle, not as paycheck cadences).
const SALARY_PAY_FREQ_OPTIONS = PAYROLL_FREQUENCY_OPTIONS.filter(
  (o) => o.value !== "daily" && o.value !== "hourly"
);

export default {
  id: "us-salary",
  name: "Salary Calculator",
  slug: "salary",
  category: "income",
  description:
    "Estimate your US take-home pay after federal tax, state tax, Social Security, Medicare, and 401(k) deductions. Built for employees, job seekers, and anyone comparing offers.",
  countries: ["US"],

  inputs: [
    {
      id: "isHourly",
      label: "Paid hourly",
      type: "toggle",
      defaultValue: false,
      helper: "Switch to enter an hourly wage instead of a salary.",
    },
    {
      id: "annualSalary",
      label: "Annual salary",
      type: "currency",
      defaultValue: 75000,
      min: 0,
      max: 10000000,
      step: 0.01,
      helper: "Your yearly salary before taxes.",
      placeholder: "0",
      visibleWhen: (v) => !v.isHourly,
    },
    {
      id: "hourlyWage",
      label: "Hourly wage",
      type: "currency",
      defaultValue: 25,
      min: 0,
      max: 1000,
      step: 0.01,
      helper: "Your hourly rate before taxes.",
      placeholder: "0",
      visibleWhen: (v) => v.isHourly === true,
    },
    {
      id: "hoursPerWeek",
      label: "Hours per week",
      type: "number",
      defaultValue: 40,
      min: 0,
      max: 168,
      step: 0.01,
      helper: "Hours you work each week.",
      visibleWhen: (v) => v.isHourly === true,
    },
    {
      id: "weeksPerYear",
      label: "Weeks per year",
      type: "number",
      defaultValue: 52,
      min: 1,
      max: 52,
      helper: "Weeks you work per year.",
      visibleWhen: (v) => v.isHourly === true,
    },
    {
      id: "filingStatus",
      label: "Filing status",
      type: "select",
      defaultValue: "single",
      options: FILING_STATUS_OPTIONS,
      helper: "Affects your tax brackets and standard deduction.",
    },
    {
      id: "state",
      label: "State",
      type: "select",
      defaultValue: "CA",
      options: STATE_OPTIONS,
      helper: "Used to estimate your state income tax.",
    },
    {
      id: "payFrequency",
      label: "Pay frequency",
      type: "select",
      defaultValue: "bi-weekly",
      options: SALARY_PAY_FREQ_OPTIONS,
      advanced: true,
      helper: "How often you receive a paycheck.",
    },
    {
      id: "retirementContribution",
      label: "401(k) contribution",
      type: "percentage",
      defaultValue: 0,
      min: 0,
      max: 100,
      step: 0.1,
      advanced: true,
      helper: "Your 401(k) contribution as a percentage of gross pay.",
    },
    {
      id: "healthInsurance",
      label: "Health insurance",
      type: "currency",
      defaultValue: 0,
      min: 0,
      max: 50000,
      step: 0.01,
      advanced: true,
      helper: "Annual health insurance premiums deducted before tax.",
      placeholder: "0",
    },
    {
      id: "otherPreTax",
      label: "Other pre-tax deductions",
      type: "currency",
      defaultValue: 0,
      min: 0,
      max: 50000,
      step: 0.01,
      advanced: true,
      helper: "Other annual pre-tax deductions (HSA, FSA, commuter, etc.).",
      placeholder: "0",
    },
    {
      id: "otherPostTax",
      label: "Other post-tax deductions",
      type: "currency",
      defaultValue: 0,
      min: 0,
      max: 50000,
      step: 0.01,
      advanced: true,
      helper: "Other annual deductions taken after tax.",
      placeholder: "0",
    },
  ],

  calculate(inputs) {
    const {
      isHourly = false,
      annualSalary = 0,
      hourlyWage = 0,
      hoursPerWeek = 40,
      weeksPerYear = 52,
      filingStatus = "single",
      state = "CA",
      payFrequency = "bi-weekly",
      retirementContribution = 0,
      healthInsurance = 0,
      otherPreTax = 0,
      otherPostTax = 0,
    } = inputs;

    // Gross annual income
    const gross = isHourly
      ? (Number(hourlyWage) || 0) * (Number(hoursPerWeek) || 0) * (Number(weeksPerYear) || 0)
      : Number(annualSalary) || 0;

    // Pre-tax deductions
    const k401Raw = (gross * (Number(retirementContribution) || 0)) / 100;
    const k401 = Math.min(k401Raw, K401_LIMIT);
    const health = Number(healthInsurance) || 0;
    const preTaxOther = Number(otherPreTax) || 0;
    const totalPreTax = k401 + health + preTaxOther;

    // FICA (Social Security + Medicare) — on gross wages
    const socialSecurity = calcSocialSecurity(gross);
    const medicare = calcMedicare(gross, filingStatus);
    const payrollTax = socialSecurity + medicare;

    // Federal income tax
    const stdDed = getStandardDeduction(filingStatus);
    const federalTaxable = Math.max(0, gross - totalPreTax - stdDed);
    const federalTax = calcFederalIncomeTax(federalTaxable, filingStatus);

    // State income tax
    const stateTaxable = Math.max(0, gross - totalPreTax);
    const stateTax = calcStateTax(stateTaxable, filingStatus, state);

    // Net pay
    const postTax = Number(otherPostTax) || 0;
    const totalTax = federalTax + stateTax + payrollTax;
    const totalDeductions = totalPreTax + totalTax + postTax;
    const netAnnual = Math.max(0, gross - totalDeductions);

    // Per-period net
    const periods = PERIODS_PER_YEAR[payFrequency] ?? 26;
    const annualHours = isHourly
      ? (Number(hoursPerWeek) || 0) * (Number(weeksPerYear) || 0)
      : DEFAULTS.ANNUAL_HOURS;
    const netHourly = annualHours > 0 ? netAnnual / annualHours : 0;
    const takeHomePct = gross > 0 ? (netAnnual / gross) * 100 : 0;

    const metrics = [
      { label: "Gross salary", value: gross, format: "currency" },
      { label: "Federal tax", value: federalTax, format: "currency" },
      { label: "State tax", value: stateTax, format: "currency" },
      { label: "Social Security", value: socialSecurity, format: "currency" },
      { label: "Medicare", value: medicare, format: "currency" },
      { label: "Total deductions", value: totalDeductions, format: "currency" },
      { label: "Net monthly pay", value: netAnnual / 12, format: "currency" },
      { label: "Net bi-weekly pay", value: netAnnual / 26, format: "currency" },
      { label: "Net weekly pay", value: netAnnual / 52, format: "currency" },
      { label: "Net hourly pay", value: netHourly, format: "currency" },
    ];

    const insights = [
      {
        tone: "info",
        title: "Your take-home rate",
        message: `You keep about ${formatPercent(takeHomePct, 0)} of your gross pay. The rest goes to federal tax, state tax, and payroll taxes.`,
      },
    ];
    if (k401 > 0) {
      insights.push({
        tone: "info",
        title: "401(k) lowers your tax bill",
        message: "Your 401(k) contribution is deducted before income tax, reducing your federal and state taxable income.",
      });
    }
    if (NO_TAX_STATES.has(state)) {
      const stateName = STATE_OPTIONS.find((o) => o.value === state)?.label || state;
      insights.push({
        tone: "success",
        title: "No state income tax",
        message: `${stateName} has no state income tax on wages, so more of your pay stays in your pocket.`,
      });
    }
    insights.push({
      tone: "warning",
      title: "Estimate only",
      message: "Your actual paycheck may differ based on employer payroll systems, tax elections, local taxes, benefits, and your individual circumstances.",
    });

    const chart = {
      id: "breakdown",
      title: "Where your money goes",
      type: "donut",
      labels: ["Take-home pay", "Federal tax", "State tax", "Payroll taxes", "Deductions"],
      series: [
        {
          name: "Amount",
          data: [
            roundCurrency(netAnnual),
            roundCurrency(federalTax),
            roundCurrency(stateTax),
            roundCurrency(payrollTax),
            roundCurrency(totalPreTax + postTax),
          ],
        },
      ],
    };

    return {
      primary: {
        label: "Estimated take-home pay",
        value: netAnnual,
        format: "currency",
        subtitle: `per year · ${formatPercent(takeHomePct, 0)} of gross`,
      },
      metrics,
      insights,
      charts: [chart],
    };
  },

  explanation: [
    {
      heading: "Gross pay vs net pay",
      body: "Gross pay is the full amount you earn before anything is taken out. Net pay — your take-home pay — is what actually lands in your bank account after federal tax, state tax, Social Security, Medicare, and any deductions are removed.",
    },
    {
      heading: "Federal income tax",
      body: "The US federal income tax is progressive, meaning you pay higher rates on higher portions of your income. Your filing status determines your tax brackets and standard deduction — a flat amount that reduces your taxable income before the brackets apply.",
    },
    {
      heading: "State income tax",
      body: "State income tax varies widely. Nine states have no income tax at all, some charge a single flat rate, and others use their own progressive brackets. This calculator estimates your state tax based on the state you select and your filing status.",
    },
    {
      heading: "Social Security",
      body: "Social Security tax is 6.2% of your wages, but only up to an annual wage limit ($184,500 in 2026). Once you earn above that limit, no further Social Security tax is taken from your paycheck for the rest of the year.",
    },
    {
      heading: "Medicare",
      body: "Medicare tax is 1.45% of all your wages, with no upper limit. If you earn over $200,000 (or $250,000 for married couples filing jointly), an additional 0.9% Medicare surcharge applies to the amount above that threshold.",
    },
    {
      heading: "Pre-tax deductions",
      body: "Pre-tax deductions — like 401(k) contributions, health insurance premiums, and HSA or FSA contributions — are taken from your pay before income tax is calculated. This lowers your taxable income, which reduces the federal and state tax you owe.",
    },
    {
      heading: "Post-tax deductions",
      body: "Post-tax deductions are taken from your pay after taxes have already been calculated. Common examples include Roth 401(k) contributions, wage garnishments, and certain benefit premiums. They reduce your take-home pay but do not lower your taxable income.",
    },
  ],

  faq: [
    {
      question: "How accurate is this salary calculator?",
      answer:
        "It provides a close estimate using 2026 federal tax brackets and FICA limits, together with state income tax estimates. Some state calculations use simplified rates rather than every deduction, credit, or local tax. Your actual paycheck may differ because of your tax elections, employer payroll systems, local taxes, and benefits. Treat the result as a guide, not a guarantee.",
    },
    {
      question: "What is the difference between gross and net pay?",
      answer:
        "Gross pay is your full earnings before anything is deducted. Net pay — your take-home pay — is what remains after federal tax, state tax, Social Security, Medicare, and any pre-tax or post-tax deductions are taken out.",
    },
    {
      question: "Why is my take-home pay lower than expected?",
      answer:
        "Your take-home pay is reduced by federal income tax, state income tax, Social Security (6.2%), Medicare (1.45%), and any deductions like 401(k) contributions or health insurance. Together these can take 20–35% or more of your gross pay, depending on your income, state, and filing status.",
    },
    {
      question: "Does this calculator include state tax?",
      answer:
        "Yes. Select your state and the calculator estimates your state income tax. Nine states — Alaska, Florida, Nevada, New Hampshire, South Dakota, Tennessee, Texas, Washington, and Wyoming — have no state income tax on wages.",
    },
    {
      question: "What if I am paid hourly?",
      answer:
        'Turn on the "Paid hourly" toggle and enter your hourly wage, hours per week, and weeks per year. The calculator multiplies these together to find your annual gross pay, then applies the same tax and deduction calculations.',
    },
    {
      question: "How is Social Security calculated?",
      answer:
        "Social Security tax is 6.2% of your gross wages, but only up to an annual limit of $184,500 in 2026. Earnings above that limit are not subject to Social Security tax for the rest of the year.",
    },
    {
      question: "What is Medicare tax?",
      answer:
        "Medicare tax is 1.45% of all your wages with no upper limit. If you earn more than $200,000 (or $250,000 if married filing jointly), an additional 0.9% is charged on the amount above that threshold.",
    },
    {
      question: "Which deductions reduce taxable income?",
      answer:
        "Pre-tax deductions — such as traditional 401(k) contributions, health insurance premiums, and HSA or FSA contributions — lower your taxable income, which reduces your federal and state income tax. Post-tax deductions, like Roth contributions, do not reduce your taxable income.",
    },
  ],

  related: ["mortgage", "loan", "compound-interest"],

  lastUpdated: "September 2026",
  formulaVersion: "1.0",
  sources: [
    { label: "2026 federal tax brackets and standard deduction — IRS", url: "https://www.irs.gov/newsroom/irs-releases-tax-inflation-adjustments-for-tax-year-2026-including-amendments-from-the-one-big-beautiful-bill" },
    { label: "2026 401(k) contribution limit — IRS", url: "https://www.irs.gov/newsroom/401k-limit-increases-to-24500-for-2026-ira-limit-increases-to-7500" },
    { label: "2026 Social Security wage base — SSA", url: "https://www.ssa.gov/oact/COLA/cbb.html" },
  ],

  seo: {
    title: "US Salary Calculator | Take-Home Pay | Fyunor",
    description:
      "Estimate your US take-home pay after federal tax, state tax, Social Security, Medicare, and 401(k) deductions. Free, accurate, and instant — no sign-up needed.",
  },
};